import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/use-workspace";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { WizardStepper } from "@/components/deal-v2/WizardStepper";
import { WizardStepRenderer } from "@/components/deal-v2/WizardStepRenderer";
import type { FieldConfig } from "@/components/deal-v2/DynamicField";
import { ArrowLeft, ArrowRight, Check, Loader2, CloudOff, Cloud } from "lucide-react";
import { toast } from "sonner";

interface StepWithFields {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  helper_text: string | null;
  step_order: number;
  fields: FieldConfig[];
}

interface DealType {
  id: string;
  name: string;
  description: string | null;
}

const STORAGE_PREFIX = "dealv2_draft_";

const DealV2Wizard = () => {
  const { dealTypeId, dealId } = useParams();
  const navigate = useNavigate();
  const { activeWorkspaceId } = useWorkspace();

  const [dealType, setDealType] = useState<DealType | null>(null);
  const [steps, setSteps] = useState<StepWithFields[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [direction, setDirection] = useState(1);
  const [saving, setSaving] = useState(false);
  const [createdDealId, setCreatedDealId] = useState<string | null>(dealId || null);
  const [showReview, setShowReview] = useState(false);
  const [dealName, setDealName] = useState("");
  const [dealNameError, setDealNameError] = useState("");
  const [autoSaveStatus, setAutoSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoSaveStatusTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const formDataRef = useRef(formData);
  const dealNameRef = useRef(dealName);

  // Keep refs in sync
  useEffect(() => { formDataRef.current = formData; }, [formData]);
  useEffect(() => { dealNameRef.current = dealName; }, [dealName]);

  // Load deal type config
  useEffect(() => {
    const loadConfig = async () => {
      const typeId = dealTypeId || null;
      let resolvedTypeId = typeId;

      // If editing, load the deal first to get deal_type_id
      if (dealId && !typeId) {
        const { data: deal } = await supabase
          .from("deals")
          .select("deal_type_id, context, name")
          .eq("id", dealId)
          .single();

        if (deal) {
          resolvedTypeId = deal.deal_type_id;
          if (deal.name) setDealName(deal.name);
          if (deal.context && typeof deal.context === "object") {
            setFormData(deal.context as Record<string, unknown>);
          }
          setCreatedDealId(dealId);
          // Clear any stale sessionStorage draft for this deal type to prevent overwrites
          if (resolvedTypeId) {
            sessionStorage.removeItem(STORAGE_PREFIX + resolvedTypeId);
          }
        }
      }

      if (!resolvedTypeId) {
        setLoading(false);
        return;
      }

      // Fetch deal type
      const { data: dt } = await supabase
        .from("deal_types")
        .select("id, name, description")
        .eq("id", resolvedTypeId)
        .single();

      if (dt) setDealType(dt);

      // Fetch steps
      const { data: stepsData } = await supabase
        .from("deal_type_steps")
        .select("*")
        .eq("deal_type_id", resolvedTypeId)
        .order("step_order");

      if (!stepsData || stepsData.length === 0) {
        setLoading(false);
        return;
      }

      // Fetch fields for all steps
      const stepIds = stepsData.map((s) => s.id);
      const { data: fieldsData } = await supabase
        .from("deal_type_fields")
        .select("*")
        .in("step_id", stepIds)
        .order("field_order");

      const stepsWithFields: StepWithFields[] = stepsData.map((s) => ({
        ...s,
        fields: (fieldsData || [])
          .filter((f) => f.step_id === s.id)
          .map((f) => ({
            ...f,
            options: Array.isArray(f.options) ? f.options as { value: string; label: string }[] : [],
            validation: f.validation as Record<string, unknown> | null,
            scan_template_id: f.scan_template_id || null,
          })),
      }));

      setSteps(stepsWithFields);

      // Populate defaults
      if (!dealId) {
        const storageKey = STORAGE_PREFIX + resolvedTypeId;
        const saved = sessionStorage.getItem(storageKey);
        if (saved) {
          try {
            setFormData(JSON.parse(saved));
          } catch { /* ignore */ }
        } else {
          const defaults: Record<string, unknown> = {};
          (fieldsData || []).forEach((f) => {
            if (f.default_value != null && f.default_value !== "") {
              defaults[f.field_key] = f.default_value;
            }
          });
          setFormData(defaults);
        }
      }

      setLoading(false);
    };

    loadConfig();
  }, [dealTypeId, dealId]);

  // Autosave to sessionStorage
  useEffect(() => {
    const key = dealTypeId || dealType?.id;
    if (key && Object.keys(formData).length > 0) {
      sessionStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(formData));
    }
  }, [formData, dealTypeId, dealType?.id]);

  // Debounced autosave to database (5 seconds after last change)
  useEffect(() => {
    if (!createdDealId || loading) return;
    if (Object.keys(formData).length === 0) return;

    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);

    autoSaveTimer.current = setTimeout(async () => {
      setAutoSaveStatus("saving");
      try {
        const { error } = await supabase
          .from("deals")
          .update({
            context: formDataRef.current as any,
            name: dealNameRef.current,
            updated_at: new Date().toISOString(),
          })
          .eq("id", createdDealId);

        if (error) throw error;
        setAutoSaveStatus("saved");

        // Clear "saved" indicator after 2 seconds
        if (autoSaveStatusTimer.current) clearTimeout(autoSaveStatusTimer.current);
        autoSaveStatusTimer.current = setTimeout(() => setAutoSaveStatus("idle"), 2000);
      } catch {
        setAutoSaveStatus("error");
      }
    }, 5000);

    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
  }, [formData, dealName, createdDealId, loading]);

  const handleChange = useCallback((key: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const validateStep = (stepIdx: number): boolean => {
    const step = steps[stepIdx];
    if (!step) return true;
    const newErrors: Record<string, string> = {};
    step.fields.forEach((f) => {
      if (f.is_required) {
        const val = formData[f.field_key];
        if (val == null || val === "" || (Array.isArray(val) && val.length === 0)) {
          newErrors[f.field_key] = "This field is required";
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveDeal = async (): Promise<string | null> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session || !activeWorkspaceId) {
      toast.error("You must be logged in with an active workspace");
      return null;
    }

    setSaving(true);
    try {
      if (createdDealId) {
        const { error } = await supabase
          .from("deals")
          .update({ context: formData as any, name: dealName, updated_at: new Date().toISOString() })
          .eq("id", createdDealId);
        if (error) throw error;
        return createdDealId;
      } else {
        const { data, error } = await supabase
          .from("deals")
          .insert({
            user_id: session.user.id,
            workspace_id: activeWorkspaceId,
            deal_type_id: dealTypeId || null,
            context: formData as any,
            name: dealName,
            status: "draft",
            strategy: "value_add",
          })
          .select("id")
          .single();
        if (error) throw error;
        setCreatedDealId(data.id);
        // Update URL without full reload
        window.history.replaceState(null, "", `/deals-v2/${data.id}`);
        return data.id;
      }
    } catch (err: any) {
      toast.error("Failed to save deal: " + err.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (!validateStep(currentStep)) return;

    // Save on every step transition
    const id = await saveDeal();
    if (!id) return;

    setCompletedSteps((prev) => new Set([...prev, currentStep]));

    if (currentStep === steps.length - 1) {
      // Last step — show review
      setShowReview(true);
    } else {
      setDirection(1);
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (showReview) {
      setShowReview(false);
      return;
    }
    if (currentStep > 0) {
      setDirection(-1);
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleComplete = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session || !createdDealId) return;

    setSaving(true);
    try {
      await supabase
        .from("deals")
        .update({ status: "complete", context: formData as any, updated_at: new Date().toISOString() })
        .eq("id", createdDealId);

      // Clean up sessionStorage
      const key = dealTypeId || dealType?.id;
      if (key) sessionStorage.removeItem(STORAGE_PREFIX + key);

      toast.success("Deal saved successfully!");
      navigate(`/deals-v2/${createdDealId}/results`);
    } catch (err: any) {
      toast.error("Failed to complete deal: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-full bg-background">
        <div className="px-4 sm:px-6 py-8 sm:py-12 max-w-4xl mx-auto space-y-6">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-96" />
          <div className="flex gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-8 w-8 rounded-full" />
            ))}
          </div>
          <Skeleton className="h-64 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  if (!dealType || steps.length === 0) {
    return (
      <div className="min-h-full bg-background">
        <div className="px-4 sm:px-6 py-8 sm:py-12 max-w-4xl mx-auto text-center">
          <h1 className="font-display text-2xl font-bold tracking-tight mb-2">Deal Type Not Found</h1>
          <p className="text-sm text-muted-foreground mb-6">
            This deal type has no configured steps yet.
          </p>
          <Button variant="outline" onClick={() => navigate("/deals-v2")}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Deals
          </Button>
        </div>
      </div>
    );
  }

  const progressPct = showReview
    ? 100
    : steps.length > 0
    ? Math.round(((currentStep + 1) / (steps.length + 1)) * 100)
    : 0;

  return (
    <div className="min-h-full bg-background">
      <div className="px-4 sm:px-6 py-8 sm:py-12 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(dealId ? `/deals-v2/${dealId}/results` : "/deals-v2")}
            className="mb-3 -ml-2 text-muted-foreground"
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> {dealId ? "Back to deal" : "Back"}
          </Button>
          <p className="font-mono text-2xs uppercase tracking-widest text-muted-foreground mb-1">{dealType.name}</p>
          {dealType.description && (
            <p className="text-sm text-muted-foreground mt-1 mb-3">{dealType.description}</p>
          )}
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
            {dealName || "Untitled Deal"}
          </h1>
        </div>

        {/* Progress */}
        <Progress value={progressPct} className="h-1.5 mb-2" />

        {/* Autosave status */}
        <div className="h-5 mb-4 flex items-center justify-end">
          {autoSaveStatus === "saving" && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> Saving…
            </span>
          )}
          {autoSaveStatus === "saved" && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Cloud className="h-3 w-3" /> Auto-saved
            </span>
          )}
          {autoSaveStatus === "error" && (
            <span className="text-xs text-destructive flex items-center gap-1">
              <CloudOff className="h-3 w-3" /> Save failed
            </span>
          )}
        </div>

        {/* Stepper */}
        <div className="mb-8">
          <WizardStepper
            steps={steps}
            currentStep={showReview ? steps.length : currentStep}
            completedSteps={completedSteps}
          />
        </div>

        {/* Content */}
        <Card>
          <CardContent className="p-6 sm:p-8">
            {showReview ? (
              <div>
                <h2 className="text-xl font-semibold text-foreground mb-4">Review & Complete</h2>
                <p className="text-sm text-muted-foreground mb-6">
                  Review your entries below before completing the deal.
                </p>
                <div className="space-y-6">
                  {steps.map((step) => (
                    <div key={step.id} className="border border-border rounded-lg p-4">
                      <h3 className="font-semibold text-sm text-foreground mb-3">{step.name}</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                        {step.fields.map((f) => {
                          const val = formData[f.field_key];
                          const display =
                            val == null || val === ""
                              ? "—"
                              : Array.isArray(val)
                              ? val.join(", ")
                              : typeof val === "boolean"
                              ? val
                                ? "Yes"
                                : "No"
                              : String(val);
                          return (
                            <div key={f.id} className="flex justify-between py-1">
                              <span className="text-xs text-muted-foreground">{f.label}</span>
                              <span className="text-sm font-medium text-foreground text-right max-w-[60%] truncate">
                                {f.unit === "$" && display !== "—" ? `$${Number(display).toLocaleString()}` : display}
                                {f.unit === "%" && display !== "—" ? "%" : ""}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <WizardStepRenderer
                step={steps[currentStep]}
                fields={steps[currentStep].fields}
                formData={formData}
                errors={errors}
                onChange={handleChange}
                direction={direction}
                dealId={createdDealId}
              />
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 0 && !showReview}
          >
            <ArrowLeft className="mr-2 h-4 w-4" /> Back
          </Button>

          {showReview ? (
            <Button onClick={handleComplete} disabled={saving}>
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Check className="mr-2 h-4 w-4" />
              )}
              Complete Deal
            </Button>
          ) : (
            <Button onClick={handleNext} disabled={saving}>
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {currentStep === steps.length - 1 ? "Review" : "Next"}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DealV2Wizard;
